import type { DataResponseBody, PageResponse, FileResponse } from "./common.types";
import type { SiteRegion } from "./sites.types";

export type EventCategory = "DETECTION" | "ROI" | "SAFETY";

export type EventType =
  | "NO_HELMET"
  | "HELMET"
  | "FALLEN_PERSON"
  | "FIRE"
  | "SMOKE"
  | "INTRUSION"
  | "EXIT"
  | "LINE_CROSSING"
  | "GAS_THRESHOLD_EXCEEDED"
  | "SOS_TRIGGERED"
  | "BAND_VITAL_ABNORMAL"
  | "BAND_FALL_DETECTED"
  | "BAND_OFFLINE";

export type EventSeverity = "UNCERTAIN" | "CAUTION" | "WARNING" | "CRITICAL";

export type EventSource = "GAS_SENSOR" | "SOS_DEVICE" | "SMART_BAND";

export interface EventSite {
  id: number;
  name: string;
  region: SiteRegion;
}

export interface Event {
  id: number;
  eventId: string;
  timestamp: string;
  category: EventCategory;
  type: EventType;
  trackId: number;
  name: string;
  confidence: number;
  path: string;
  site: EventSite;
  snapshot?: FileResponse;
  video?: FileResponse;
  severity?: EventSeverity;
  source?: EventSource;
  deviceId?: string;
  bandId?: string;
  lat?: number;
  lon?: number;
  alt?: number;
  accuracyM?: number;
  payload?: Record<string, unknown>;
}

export type EventsResponse = DataResponseBody<PageResponse<Event>>;
export type EventDetailResponse = DataResponseBody<Event>;

export interface GetEventsParams {
  page?: number;
  size?: number;
  siteId?: number;
  query?: string;
}

export interface EventTypeInfo {
  name: EventType;
  displayName: string;
}

export interface EventCategoryInfo {
  name: EventCategory;
  displayName: string;
  types: EventTypeInfo[];
}

export type EventCategoriesResponse = DataResponseBody<EventCategoryInfo[]>;

export type EventSeverityBucket = "critical" | "warning" | "uncertain" | "info";

const SEVERITY_BUCKET: Record<EventSeverity, EventSeverityBucket> = {
  CRITICAL: "critical",
  WARNING: "warning",
  UNCERTAIN: "uncertain",
  CAUTION: "uncertain",
};

export const EVENT_TYPE_SEVERITY: Record<EventType, EventSeverityBucket> = {
  NO_HELMET: "critical",
  HELMET: "info",
  FALLEN_PERSON: "critical",
  FIRE: "critical",
  SMOKE: "critical",
  INTRUSION: "warning",
  EXIT: "warning",
  LINE_CROSSING: "warning",
  GAS_THRESHOLD_EXCEEDED: "critical",
  SOS_TRIGGERED: "critical",
  BAND_VITAL_ABNORMAL: "critical",
  BAND_FALL_DETECTED: "critical",
  BAND_OFFLINE: "warning",
};

export function eventSeverity(event: Pick<Event, "type" | "severity">): EventSeverityBucket {
  if (event.severity) return SEVERITY_BUCKET[event.severity] ?? EVENT_TYPE_SEVERITY[event.type];
  return EVENT_TYPE_SEVERITY[event.type];
}

export function isUncertainEvent(event: Pick<Event, "type" | "severity">): boolean {
  return eventSeverity(event) === "uncertain";
}

export const EVENT_SEVERITY_STYLES = {
  critical: { bg: "bg-red-100", text: "text-red-700", label: "위험" },
  warning: { bg: "bg-orange-100", text: "text-orange-700", label: "경고" },
  uncertain: { bg: "bg-amber-100", text: "text-amber-700", label: "추정" },
  info: { bg: "bg-blue-100", text: "text-blue-700", label: "정보" },
} as const;

export const EVENT_REGION_MAP: Record<SiteRegion, string> = {
  SEOUL: "서울",
  GYEONGGI_INCHEON: "경기",
  GANGWON: "강원",
  CHUNGCHEONG: "충청",
  JEOLLA: "전라",
  GYEONGSANG: "경상",
  JEJU: "제주",
};

export const EVENT_REGIONS = ["전체", ...Object.values(EVENT_REGION_MAP)] as const;
export type EventRegionTab = (typeof EVENT_REGIONS)[number];

export const EVENT_REGION_LABEL_TO_KEY: Record<string, SiteRegion> = Object.fromEntries(
  Object.entries(EVENT_REGION_MAP).map(([key, label]) => [label, key as SiteRegion])
);

export type StompEventType = EventType;
export type StompEventCategory = EventCategory;

export interface StompEventResponse {
  eventId: string;
  id: number;
  name: string;
  type: StompEventType;
  category: StompEventCategory;
  confidence: number;
  timestamp: string;
  trackId: number;
  path: string;
  snapshot?: FileResponse;
  video?: FileResponse;
  severity?: EventSeverity;
}

export interface SiteEmergencyPayload {
  workerId: string;
  position: { lng: number; lat: number; altitude: number };
  vitals: { temperature: number; heartRate: number };
}

export interface SiteEvent extends Event {
  emergency?: SiteEmergencyPayload;
}
